import { BASKET_CHANGED } from '../topics';

declare global {
    interface Window {
        dataLayer: any[];
    }
}

declare const dataLayer: any[];

if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
}

import { FunctionComponent } from 'preact';
import { useMemo, useState } from 'preact/hooks';
import currency from 'currency.js';
import Api from '../common/api';
import BuyingOptionsPagination from './BuyingOptionsPagination';
import BuyingOptionsFloatingPagination from './BuyingOptionsFloatingPagination';
import BuyingOptionsTable from './BuyingOptionsTable';
import BuyingOptionsSummary from './BuyingOptionsSummary';
import Messages from './Messages';
import { ProductVariant, Image, BasketItem } from '../types';
import debounce from 'lodash/debounce';

const BuyingOptions: FunctionComponent<BuyingOptionsProps> = ({
    table,
    productVariants,
    images,
    multiple,
    alias,
    productName,
    isSpecialKnitwear,
    isSchoolPage
}) => {
    if (productVariants.length === 0) {
        return null;
    }

    const missingPricing = productVariants.some(pv => pv.unit_price === null);

    // Basket states
    const initialBasketItems: BasketItem[] = [];
    const [productBasket, setProductBasket] = useState(initialBasketItems);
    const [isProcessing, setIsProcessing] = useState(false);
    // Message states
    const initialErrorMessage: string = '';
    const [errorMessage, setErrorMessage] = useState(initialErrorMessage);
    const initialWarningMessage: string = (missingPricing) ? 'Pricing currently isn\'t available for this product, however you can still order as usual' : '';
    const [warningMessage, setWarningMessage] = useState(initialWarningMessage);
    const initialSuccessMessage: string = '';
    const [successMessage, setSuccessMessage] = useState(initialSuccessMessage);
    // Sticky Table state
    const [stickyTableVisibility, setStickyTableVisibility] = useState(false);
    // Pagination button state
    const [nextButtonIsDisabled, setNextButtonIsDisabled] = useState(false);
    const [prevButtonIsDisabled, setPrevButtonIsDisabled] = useState(false);

    // Number of product items in table
    const productItemCount = useMemo<number>(
        () => productBasket.reduce((qty, bi): number => qty + bi.quantity, 0),
        [productBasket]
    );

    //
    // WARNING currency manipulation
    // Total price of this product currently in the basket (excluding surcharges)
    const productPriceTotal = useMemo<currency>(
        () =>
            productBasket.reduce((p, bi) => {
                const itemTotal = currency(bi.unit_price ?? '0.00').multiply(bi.quantity);

                return p.add(itemTotal);
            }, currency('0.00')),
        [productBasket]
    );

    // API call and GA4 dataLayer updates for adding a variant to the basket
    const addToBasket = () => {
        setIsProcessing(true);

        if (productBasket.length === 0) {
            setWarningMessage('Please select items to add to your basket.');
            setIsProcessing(false);
            return;
        }

        (window as any).dataLayer = (window as any).dataLayer || [];
        window.dataLayer = window.dataLayer || [];
        dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
            event: 'add_to_cart',
            ecommerce: {
                add: {
                    products: [productBasket]
                }
            },
            ga4_ecommerce: {
                items: [productBasket]
            }
        });

        Api.addManyItemsPromise(alias, productBasket)
            .then(extBasket => {
                setProductBasket(initialBasketItems);
                setErrorMessage(initialErrorMessage);
                setWarningMessage(initialWarningMessage);
                setSuccessMessage('Items successfully added to basket.');
                setIsProcessing(false);

                PubSub.publish(BASKET_CHANGED, extBasket);
            })
            .catch(e => {
                console.error(e);
                setSuccessMessage(initialSuccessMessage);
                setErrorMessage('Unable to add item to the basket.');
                setIsProcessing(false);
            })
            .then(e => {
                const alertsWrapper = table.querySelector('#js-alerts-wrapper');
                if (alertsWrapper !== null) {
                    alertsWrapper.scrollIntoView({ behavior: 'smooth' });
                }
            });
    };

    const onChangeQuantity = (selectedVariant, quantity) => {
        const sku = selectedVariant.sku_alias || selectedVariant.sku;
        const existingIdx = productBasket.findIndex(bi => bi.sku === sku);
        const newProductBasket = productBasket.slice(0);

        const newBasketItem: BasketItem = {
            product_name: productName,
            url: '',
            sku,
            unit_price: selectedVariant.unit_price + '',
            //
            // WARNING currency manipulation
            //
            subtotal: currency(selectedVariant.unit_price + '', { symbol: '' })
                .multiply(quantity)
                .format(),
            tax: selectedVariant.tax + '',
            total: selectedVariant.total + '',
            size: selectedVariant.size + '',
            colour: selectedVariant.colour + '',
            multiple,
            quantity
        };

        if (existingIdx === -1 && quantity > 0) {
            newProductBasket.push(newBasketItem);
            setProductBasket(newProductBasket);
        } else if (existingIdx !== -1 && quantity > 0) {
            newProductBasket[existingIdx] = newBasketItem;
            setProductBasket(newProductBasket);
        } else if (existingIdx !== -1) {
            newProductBasket.splice(existingIdx, 1);
            setProductBasket(newProductBasket);
        }
    };

    // Scroll right logic for pagination
    const scrollRight = () => {
        const tableContainer = table.querySelector<HTMLElement>('[data-buying-options-container]');

        if (tableContainer === null) {
            return;
        }

        const colourColumnWidth = table.querySelector<HTMLAnchorElement>(
            '.buying-options__table-head-cell'
        )?.offsetWidth;
        tableContainer.scrollLeft =
            tableContainer.scrollLeft + (tableContainer.offsetWidth - (colourColumnWidth ?? 170));
    };

    // Scroll left logic for pagination
    const scrollLeft = () => {
        const tableContainer = table.querySelector<HTMLElement>('[data-buying-options-container]');

        if (tableContainer === null) {
            return;
        }

        const colourColumnWidth = table.querySelector<HTMLAnchorElement>(
            '.buying-options__table-head-cell'
        )?.offsetWidth;
        tableContainer.scrollLeft =
            tableContainer.scrollLeft - (tableContainer.offsetWidth - (colourColumnWidth ?? 170));
    };

    // Logic to disable and enable the pagination buttons
    const disablePaginationButtonsCheck = () => {
        const tableContainer = table.querySelector<HTMLElement>('[data-buying-options-container]');

        if (tableContainer === null) {
            return;
        }

        const tableContainerWidth = tableContainer.offsetWidth;
        const tableWidth = table.querySelector<HTMLElement>('table')?.offsetWidth ?? 0;
        const tablePosition = tableContainer.scrollLeft;

        setNextButtonIsDisabled(tableWidth - tablePosition - tableContainerWidth < 2);
        setPrevButtonIsDisabled(tablePosition === 0);
    };

    // Sync the scroll position between the normal table and the sticky table
    const syncScroll = selectedTable => {
        const stickyTableContainer = selectedTable.parentElement.querySelector(
            '[data-buying-options-container-sticky]'
        );
        if (selectedTable && stickyTableContainer) {
            stickyTableContainer.scrollLeft = selectedTable.scrollLeft;
        }
    };

    // Set sticky tables cell widths
    const setStickyTableCellWidths = () => {
        const normalTable = table.querySelector<HTMLElement>('[data-table-normal-head]');
        const stickyTable = table.querySelector<HTMLElement>('[data-table-sticky-head]');
        if (normalTable && stickyTable) {
            const thNormal = normalTable.querySelectorAll('thead th');
            const thSticky = stickyTable.querySelectorAll('th');
            thNormal.forEach((th, index) => {
                const width = th.getBoundingClientRect().width;
                thSticky[index].style.minWidth = `${width}px`;
            });
        }
    };

    // Set widths of variant cells
    const setVariantCellWidth = () => {
        const tableContainer = table.querySelector<HTMLElement>('.buying-options__table-container');

        if (tableContainer === null) {
            return;
        }

        // Calculate the variant cell width for each breakpoint
        const tableWidth = tableContainer.offsetWidth;
        const colourColumnWidth = table.querySelector<HTMLElement>(
            '.buying-options__table-head-cell'
        )?.offsetWidth;
        if (window.innerWidth < 992) {
            var variantCellWidth = (tableWidth - (colourColumnWidth ?? 170)) / 4;
        } else if (window.innerWidth < 1200) {
            var variantCellWidth = (tableWidth - (colourColumnWidth ?? 170)) / 5;
        } else {
            var variantCellWidth = (tableWidth - (colourColumnWidth ?? 170)) / 7;
        }

        const variantCells = table.querySelectorAll<HTMLElement>(
            '.buying-options__table-variant-cell'
        );
        variantCells.forEach(variantCell => {
            variantCell.style.minWidth = variantCellWidth.toString() + 'px';
            const variantCellInput = variantCell.querySelector<HTMLElement>(
                '.buying-options__quantity-input'
            );
            const variantCellInputWidth = variantCellWidth < 120 ? 100 : variantCellWidth - 22;
            variantCellInput !== null
                ? (variantCellInput.style.width = variantCellInputWidth.toString() + 'px')
                : null;
        });
    };

    // Toggle the visibility of the sticky table
    const toggleStickyTableVisibility = () => {
        // Exit this function when on a school page as multiple buying option tables are rendered on the these pages.
        if (isSchoolPage) {
            return;
        }

        const buyingOptionsPanel = table.closest<HTMLElement>('[data-buying-options-panel]');
        if (window.getComputedStyle(buyingOptionsPanel as HTMLElement).display === 'none') {
            return;
        }
        const tableContainer = table.querySelector<HTMLElement>('[data-buying-options-container]');
        const normalTable = table.querySelector<HTMLElement>('[data-table-normal-head]');
        const tableOffset = normalTable
            ? normalTable.getBoundingClientRect().top + window.scrollY
            : 0;
        const stickyTable = table.querySelector<HTMLElement>('[data-table-sticky-head]');
        const offset = window.scrollY;
        setStickyTableVisibility(offset >= tableOffset && stickyTable !== null);
        syncScroll(tableContainer);
    };

    window.addEventListener('scroll', toggleStickyTableVisibility);

    // Set the table cell sizes, check disabled buttons and sticky table after the page has loaded an on page resize
    window.addEventListener('load', () => {
        setVariantCellWidth();
        disablePaginationButtonsCheck();
        setStickyTableCellWidths();
    });
    window.addEventListener(
        'resize',
        debounce(() => {
            setVariantCellWidth();
            setStickyTableCellWidths();
            disablePaginationButtonsCheck();
            toggleStickyTableVisibility();
        }, 100)
    );

    return (
        <div class="buying-options">
            <div class="d-flex justify-content-between mb-5">
                <h2 class="d-none d-md-block font-weight-bold mb-0">Buying Options</h2>
                <BuyingOptionsPagination
                    scrollLeft={scrollLeft}
                    scrollRight={scrollRight}
                    syncScroll={syncScroll}
                    disablePaginationButtonsCheck={disablePaginationButtonsCheck}
                    nextButtonIsDisabled={nextButtonIsDisabled}
                    prevButtonIsDisabled={prevButtonIsDisabled}
                />
            </div>
            <Messages
                success={successMessage}
                error={errorMessage}
                warning={warningMessage}
                isSpecialKnitwear={isSpecialKnitwear}
            />
            <div class="position-relative">
                <BuyingOptionsTable
                    productVariants={productVariants}
                    images={images}
                    isSpecialKnitwear={isSpecialKnitwear}
                    productBasket={productBasket}
                    multiple={multiple}
                    isProcessing={isProcessing}
                    onChangeQuantity={onChangeQuantity}
                    scrollRight={scrollRight}
                    nextButtonIsDisabled={nextButtonIsDisabled}
                    stickyTableVisibility={stickyTableVisibility}
                    isSchoolPage={isSchoolPage}
                    hidePrices={missingPricing}
                />
            </div>
            {isSchoolPage === false ? (
                <BuyingOptionsFloatingPagination
                    scrollLeft={scrollLeft}
                    scrollRight={scrollRight}
                    syncScroll={syncScroll}
                    disablePaginationButtonsCheck={disablePaginationButtonsCheck}
                    nextButtonIsDisabled={nextButtonIsDisabled}
                    prevButtonIsDisabled={prevButtonIsDisabled}
                />
            ) : null}
            <BuyingOptionsSummary
                rows={productBasket.length}
                items={productItemCount}
                price={productPriceTotal.toString()}
                addToBasket={addToBasket}
                productItemCount={productItemCount}
                hideTotal={missingPricing}
                isProcessing={isProcessing}
            />
        </div>
    );
};

interface BuyingOptionsProps {
    table: HTMLDivElement;
    productVariants: ProductVariant[];
    images: Image[];
    multiple: number;
    alias: string;
    productName: string;
    isSpecialKnitwear: boolean;
    isSchoolPage: boolean;
}

export default BuyingOptions;
